import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../services/api';

const token = () => localStorage.getItem('token');
const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` });

// ─── Helpers ─────────────────────────────────────────────────────────────────
function fmt(n) { return (parseFloat(n) || 0).toFixed(2); }
function fmtDate(d) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const TYPE_COLORS = {
  ENTREE: 'bg-green-100 text-green-800',
  SORTIE: 'bg-red-100 text-red-800',
  RETOUR: 'bg-blue-100 text-blue-800',
  AJUSTEMENT: 'bg-yellow-100 text-yellow-800',
  entree: 'bg-green-100 text-green-800',
  sortie: 'bg-red-100 text-red-800',
  ajustement: 'bg-yellow-100 text-yellow-800',
};

function Badge({ type }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[type] || 'bg-gray-100 text-gray-700'}`}>
      {type}
    </span>
  );
}

// ─── Tableau mouvements ───────────────────────────────────────────────────────
function MouvementsTable({ mouvements }) {
  if (!mouvements?.length) {
    return <p className="text-gray-400 text-sm py-4 text-center">Aucun mouvement enregistré</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 px-3 text-gray-500 font-medium">Date</th>
            <th className="text-left py-2 px-3 text-gray-500 font-medium">Type</th>
            <th className="text-right py-2 px-3 text-gray-500 font-medium">Qté</th>
            <th className="text-left py-2 px-3 text-gray-500 font-medium">Bijou</th>
            <th className="text-left py-2 px-3 text-gray-500 font-medium hidden sm:table-cell">Pièce / OF</th>
            <th className="text-left py-2 px-3 text-gray-500 font-medium hidden md:table-cell">Client</th>
          </tr>
        </thead>
        <tbody>
          {mouvements.slice(0, 15).map((mv, idx) => {
            const isSortie = mv.type_mouvement === 'SORTIE';
            const hasRef = mv.reference_produit || mv.id_produit || mv.numero_ordre;
            return (
            <tr key={`${mv.id}-${idx}`} className={`border-b border-gray-100 hover:bg-gray-50 ${isSortie ? 'bg-red-50/30' : ''}`}>
              <td className="py-2 px-3 text-gray-600 whitespace-nowrap">{fmtDate(mv.date_mouvement)}</td>
              <td className="py-2 px-3"><Badge type={mv.type_mouvement} /></td>
              <td className={`py-2 px-3 text-right font-mono font-bold ${isSortie ? 'text-red-600' : 'text-green-600'}`}>
                {isSortie ? '-' : '+'}{fmt(mv.quantite)}g
              </td>
              <td className="py-2 px-3">
                {mv.reference_produit ? (
                  <span className="font-semibold text-gray-800">{mv.reference_produit}</span>
                ) : (
                  <span className="text-gray-400 text-xs">{mv.motif || '-'}</span>
                )}
              </td>
              <td className="py-2 px-3 hidden sm:table-cell">
                {mv.id_produit ? (
                  <div className="flex flex-col gap-0.5">
                    <span className="font-mono text-xs font-semibold text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">
                      pièce {mv.id_produit}
                    </span>
                    {mv.numero_ordre && (
                      <span className="text-xs text-gray-400">{mv.numero_ordre}</span>
                    )}
                  </div>
                ) : mv.numero_ordre ? (
                  <span className="text-xs text-gray-500">{mv.numero_ordre}</span>
                ) : mv.numero_devis ? (
                  <span className="text-xs text-gray-400">{mv.numero_devis}</span>
                ) : (
                  <span className="text-gray-300 text-xs">—</span>
                )}
              </td>
              <td className="py-2 px-3 text-gray-400 hidden md:table-cell truncate max-w-xs text-xs">
                {mv.client_nom || mv.notes || '-'}
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Card stock (Or ou Pierre) ────────────────────────────────────────────────
function StockCard({ stock, maxQty }) {
  const [open, setOpen] = useState(false);
  const pct = maxQty > 0 ? Math.min((stock.quantite_disponible / maxQty) * 100, 100) : 0;
  const couleur = pct > 50 ? '#22c55e' : pct > 20 ? '#f59e0b' : '#ef4444';

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div
        className="p-4 cursor-pointer flex items-center justify-between"
        onClick={() => setOpen(!open)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-sm text-gray-900 truncate">{stock.reference}</span>
            {stock.purete && (
              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full">{stock.purete}</span>
            )}
            {stock.couleur_or && (
              <span className="bg-amber-50 text-amber-700 text-xs px-2 py-0.5 rounded-full">{stock.couleur_or}</span>
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">{stock.designation || stock.type_matiere}</p>
          {/* Barre de progression */}
          <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: couleur }} />
          </div>
        </div>
        <div className="ml-4 text-right flex-shrink-0">
          <div className="text-lg font-bold" style={{ color: couleur }}>{fmt(stock.quantite_disponible)}</div>
          <div className="text-xs text-gray-400">{stock.unite || 'g'}</div>
          {stock.mouvements?.length > 0 && (
            <div className="text-xs text-gray-400 mt-1">{stock.mouvements.length} mvts</div>
          )}
        </div>
        <span className="ml-3 text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div className="border-t border-gray-100 px-4 pb-4">
          <div className="flex items-center justify-between py-2 mb-2">
            <span className="text-xs text-gray-500 font-medium">10 derniers mouvements</span>
            <span className="text-xs text-gray-400">
              Prix achat: {stock.prix_achat_unitaire ? `${fmt(stock.prix_achat_unitaire)} MAD/g` : 'N/A'}
            </span>
          </div>
          <MouvementsTable mouvements={stock.mouvements} />
        </div>
      )}
    </div>
  );
}

// ─── Onglet Or / Pierre ───────────────────────────────────────────────────────
function TabStocks({ url, categorie }) {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_URL}/exports/stock-matieres?categorie=${categorie}`, {
        headers: headers(),
      });
      if (!res.ok) throw new Error('Erreur export');
      const blob = await res.blob();
      const url2 = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url2;
      const d = new Date();
      const ds = `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}`;
      a.download = `stock_${categorie.toLowerCase()}_${ds}.xlsx`;
      a.click();
      URL.revokeObjectURL(url2);
    } catch (e) {
      alert('Erreur lors de l\'export : ' + e.message);
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}${url}`, { headers: headers() });
      if (!res.ok) throw new Error('Erreur réseau');
      const data = await res.json();
      setStocks(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-12 text-gray-400">Chargement...</div>;
  if (error) return <div className="text-red-500 text-center py-8">{error}</div>;
  if (!stocks.length) return <div className="text-gray-400 text-center py-12">Aucun stock trouvé</div>;

  const maxQty = Math.max(...stocks.map(s => s.quantite_disponible), 1);
  const totalDispo = stocks.reduce((a, s) => a + (s.quantite_disponible || 0), 0);
  const totalMvts = stocks.reduce((a, s) => a + (s.mouvements?.length || 0), 0);

  return (
    <div>
      {/* Résumé + bouton export */}
      <div className="flex flex-wrap items-start gap-3 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 flex-1">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-xs text-gray-500 mb-1">Total en stock</div>
            <div className="text-xl font-bold text-gray-900">{fmt(totalDispo)} g</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="text-xs text-gray-500 mb-1">Références</div>
            <div className="text-xl font-bold text-gray-900">{stocks.length}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 col-span-2 md:col-span-1">
            <div className="text-xs text-gray-500 mb-1">Mouvements tracés</div>
            <div className="text-xl font-bold text-gray-900">{totalMvts}</div>
          </div>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl shadow-sm disabled:opacity-50 whitespace-nowrap"
        >
          {exporting ? '⏳ Export...' : '⬇️ Exporter Excel'}
        </button>
      </div>

      {/* Cards */}
      <div className="space-y-3">
        {stocks.map(s => <StockCard key={s.id} stock={s} maxQty={maxQty} />)}
      </div>
    </div>
  );
}

// ─── Modal Retour Matière ─────────────────────────────────────────────────────
function ModalRetour({ ouvrier, stocks, onClose, onSuccess }) {
  const [form, setForm] = useState({
    stock_matiere_id: '',
    quantite_retournee: '',
    quantite_utilisee: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/stock-tracabilite/retour-ouvrier`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          ouvrier_id: ouvrier.id,
          stock_matiere_id: parseInt(form.stock_matiere_id),
          quantite_retournee: parseFloat(form.quantite_retournee) || 0,
          quantite_utilisee: parseFloat(form.quantite_utilisee) || 0,
          notes: form.notes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      onSuccess(data.message);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="p-5 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">
            Retour matière — {ouvrier.prenom} {ouvrier.nom}
          </h3>
          <p className="text-xs text-gray-500 mt-1">Coffre: {fmt(ouvrier.total_grammes)}g</p>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Matière</label>
            <select
              required
              value={form.stock_matiere_id}
              onChange={e => setForm(f => ({ ...f, stock_matiere_id: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <option value="">Sélectionner...</option>
              {stocks.map(s => (
                <option key={s.id} value={s.id}>{s.reference} — {fmt(s.quantite_disponible)}g dispo</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Quantité retournée (g)</label>
              <input
                type="number" step="0.01" min="0"
                value={form.quantite_retournee}
                onChange={e => setForm(f => ({ ...f, quantite_retournee: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Quantité utilisée (g)</label>
              <input
                type="number" step="0.01" min="0"
                value={form.quantite_utilisee}
                onChange={e => setForm(f => ({ ...f, quantite_utilisee: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="0.00"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
              placeholder="Référence OF, observations..."
            />
          </div>
          {error && <div className="text-red-500 text-sm bg-red-50 rounded-lg p-3">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              Annuler
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 disabled:opacity-50">
              {saving ? 'Enregistrement...' : 'Confirmer retour'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Onglet Coffres Ouvriers ──────────────────────────────────────────────────
function TabOuvriers({ allStocks }) {
  const [ouvriers, setOuvriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOuvrier, setModalOuvrier] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/stock-tracabilite/ouvriers`, { headers: headers() });
      if (!res.ok) throw new Error('Erreur réseau');
      setOuvriers(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSuccess = (msg) => {
    setModalOuvrier(null);
    setSuccessMsg(msg);
    load();
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  if (loading) return <div className="flex justify-center py-12 text-gray-400">Chargement...</div>;
  if (error) return <div className="text-red-500 text-center py-8">{error}</div>;

  return (
    <div>
      {successMsg && (
        <div className="mb-4 bg-green-50 text-green-700 border border-green-200 rounded-xl p-3 text-sm">
          {successMsg}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {ouvriers.map(o => (
          <div key={o.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-semibold text-sm text-gray-900">{o.prenom} {o.nom}</div>
                <div className="text-xs text-gray-400">{o.specialite || 'Ouvrier'}</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-amber-600">{fmt(o.total_grammes)}g</div>
                <div className="text-xs text-gray-400">{o.nb_entrees} article(s)</div>
              </div>
            </div>

            {/* Détail coffre */}
            {o.coffre_detail?.length > 0 && (
              <div className="mb-3 space-y-1">
                {o.coffre_detail.map((c, i) => (
                  <div key={i} className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-1.5">
                    <span className="text-xs font-medium text-amber-800">{c.reference}</span>
                    <span className="text-xs text-amber-700 font-mono">{fmt(c.quantite_grammes)}g</span>
                  </div>
                ))}
              </div>
            )}

            {/* Mouvements récents */}
            {o.mouvements?.length > 0 && (
              <div className="border-t border-gray-100 pt-3">
                <div className="text-xs text-gray-500 font-medium mb-2">Derniers mouvements</div>
                <div className="space-y-1">
                  {o.mouvements.slice(0, 5).map(mv => (
                    <div key={mv.id} className="flex items-center justify-between text-xs">
                      <span className="text-gray-400">{fmtDate(mv.date_mouvement)}</span>
                      <Badge type={mv.type_mouvement} />
                      <span className="font-mono text-gray-700">{fmt(mv.quantite)}g</span>
                      <span className="text-gray-400 truncate max-w-[80px]">{mv.matiere_reference || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {o.total_grammes > 0 && (
              <button
                onClick={() => setModalOuvrier(o)}
                className="mt-3 w-full py-2 bg-blue-500 text-white rounded-lg text-xs font-medium hover:bg-blue-600 transition-colors"
              >
                Retour matière
              </button>
            )}
          </div>
        ))}
      </div>

      {modalOuvrier && (
        <ModalRetour
          ouvrier={modalOuvrier}
          stocks={allStocks}
          onClose={() => setModalOuvrier(null)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}

// ─── Onglet Donner matière ────────────────────────────────────────────────────
function TabDonner({ allStocks }) {
  const [ouvriers, setOuvriers] = useState([]);
  const [form, setForm] = useState({
    stock_matiere_id: '',
    ouvrier_id: '',
    quantite: '',
    notes: '',
    ordre_fabrication_id: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/ouvriers`, { headers: headers() })
      .then(r => r.json())
      .then(d => setOuvriers(Array.isArray(d) ? d : (d.ouvriers || [])))
      .catch(() => {});
  }, []);

  const selectedStock = allStocks.find(s => s.id === parseInt(form.stock_matiere_id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API_URL}/stock-tracabilite/donner-ouvrier`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          stock_matiere_id: parseInt(form.stock_matiere_id),
          ouvrier_id: parseInt(form.ouvrier_id),
          quantite: parseFloat(form.quantite),
          notes: form.notes,
          ordre_fabrication_id: form.ordre_fabrication_id ? parseInt(form.ordre_fabrication_id) : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      setSuccess(data.message);
      setForm({ stock_matiere_id: '', ouvrier_id: '', quantite: '', notes: '', ordre_fabrication_id: '' });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto">
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h3 className="font-semibold text-gray-900 mb-5">Donner matière à un ouvrier</h3>

        {success && (
          <div className="mb-4 bg-green-50 text-green-700 border border-green-200 rounded-xl p-3 text-sm">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Matière *</label>
            <select
              required
              value={form.stock_matiere_id}
              onChange={e => setForm(f => ({ ...f, stock_matiere_id: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <option value="">Sélectionner une matière...</option>
              {allStocks.map(s => (
                <option key={s.id} value={s.id}>
                  {s.reference} ({s.categorie}) — {fmt(s.quantite_disponible)}g dispo
                </option>
              ))}
            </select>
            {selectedStock && (
              <p className="text-xs text-gray-400 mt-1">
                Stock disponible: <strong>{fmt(selectedStock.quantite_disponible)}{selectedStock.unite || 'g'}</strong>
                {selectedStock.prix_achat_unitaire ? ` — ${fmt(selectedStock.prix_achat_unitaire)} MAD/g` : ''}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Ouvrier *</label>
            <select
              required
              value={form.ouvrier_id}
              onChange={e => setForm(f => ({ ...f, ouvrier_id: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            >
              <option value="">Sélectionner un ouvrier...</option>
              {ouvriers.map(o => (
                <option key={o.id} value={o.id}>{o.prenom} {o.nom} — {o.specialite || 'Ouvrier'}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Quantité (g) *</label>
              <input
                type="number" step="0.01" min="0.01" required
                value={form.quantite}
                onChange={e => setForm(f => ({ ...f, quantite: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">N° OF (optionnel)</label>
              <input
                type="number"
                value={form.ordre_fabrication_id}
                onChange={e => setForm(f => ({ ...f, ordre_fabrication_id: e.target.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                placeholder="ID ordre"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
              placeholder="Référence commande, bijou concerné..."
            />
          </div>

          {error && <div className="text-red-500 text-sm bg-red-50 rounded-lg p-3">{error}</div>}

          <button
            type="submit"
            disabled={saving}
            className="w-full py-3 rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #d4af37, #c49b30)', color: '#0a0a0a' }}
          >
            {saving ? 'Enregistrement...' : 'Donner la matière'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
const TABS = [
  { key: 'or',       label: '🥇 Or',        icon: '🥇' },
  { key: 'diamants', label: '💎 Diamants',   icon: '💎' },
  { key: 'pierres',  label: '💠 Pierres',    icon: '💠' },
  { key: 'ouvriers', label: '👷 Coffres',    icon: '👷' },
];

export default function StockTracabilite() {
  const [tab, setTab] = useState('or');
  const [allStocks, setAllStocks] = useState([]);
  const [prixOrActuel, setPrixOrActuel] = useState(null);

  // Charger tous les stocks (or + pierres + diamants) pour les selects
  useEffect(() => {
    const loadAll = async () => {
      try {
        const [rOr, rPierre, rDia, rPrix] = await Promise.all([
          fetch(`${API_URL}/stock-tracabilite/or`, { headers: headers() }),
          fetch(`${API_URL}/stock-tracabilite/pierres`, { headers: headers() }),
          fetch(`${API_URL}/stock-tracabilite/diamants`, { headers: headers() }),
          fetch(`${API_URL}/stock-matieres/prix/actuel`, { headers: headers() }),
        ]);
        const [or, pierre, dia] = await Promise.all([rOr.json(), rPierre.json(), rDia.json()]);
        setAllStocks([
          ...(Array.isArray(or) ? or : []),
          ...(Array.isArray(pierre) ? pierre : []),
          ...(Array.isArray(dia) ? dia : []),
        ]);
        try { const prix = await rPrix.json(); setPrixOrActuel(prix); } catch(e) {}
      } catch (e) {
        console.error('Erreur chargement stocks:', e);
      }
    };
    loadAll();
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#f8f7f4' }}>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Suivi Stock Matieres</h1>
          <p className="text-sm text-gray-500 mt-1">Tracabilite complete — Or · Diamants · Pierres · Ouvriers</p>
        </div>
        {prixOrActuel && (
          <div className="flex gap-3">
            {prixOrActuel.or && <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-2 text-center">
              <p className="text-[10px] text-yellow-600 font-bold uppercase">Or 18K</p>
              <p className="text-lg font-black text-yellow-700">{parseFloat(prixOrActuel.or).toLocaleString('fr-FR')} DH/g</p>
            </div>}
            {prixOrActuel.chaine && <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2 text-center">
              <p className="text-[10px] text-amber-600 font-bold uppercase">Chaine</p>
              <p className="text-lg font-black text-amber-700">{parseFloat(prixOrActuel.chaine).toLocaleString('fr-FR')} DH/g</p>
            </div>}
            {prixOrActuel.mousqueton && <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-2 text-center">
              <p className="text-[10px] text-orange-600 font-bold uppercase">Mousqueton</p>
              <p className="text-lg font-black text-orange-700">{parseFloat(prixOrActuel.mousqueton).toLocaleString('fr-FR')} DH/g</p>
            </div>}
          </div>
        )}
      </div>

      {/* Onglets */}
      <div className="flex gap-1 bg-white rounded-xl border border-gray-200 p-1 mb-6 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 min-w-max py-2 px-3 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
              tab === t.key
                ? 'text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            style={tab === t.key
              ? t.key === 'diamants'
                ? { background: 'linear-gradient(135deg, #b8d0e8, #7fb3d3)', color: '#1a3a5c' }
                : t.key === 'pierres'
                ? { background: 'linear-gradient(135deg, #d8b4fe, #a855f7)', color: '#fff' }
                : { background: 'linear-gradient(135deg, #d4af37, #c49b30)', color: '#0a0a0a' }
              : {}}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      {tab === 'or'       && <TabStocks url="/stock-tracabilite/or"       categorie="METAL"  unite="g"  />}
      {tab === 'diamants' && <TabStocks url="/stock-tracabilite/diamants" categorie="PIERRE" unite="ct" />}
      {tab === 'pierres'  && <TabStocks url="/stock-tracabilite/pierres"  categorie="PIERRE" unite="ct" />}
      {tab === 'ouvriers' && <TabOuvriers allStocks={allStocks} />}
    </div>
  );
}
